import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Users, TrendingUp, MapPin, Phone, Mail, ArrowRight, Star, Globe, Handshake, Target, Award } from "lucide-react";

const About = () => {
  const expertise = [
    {
      icon: Users,
      title: "Tenant Representation",
      description: "Helping businesses find the perfect commercial space"
    },
    {
      icon: TrendingUp,
      title: "Investment Sales",
      description: "Identifying undervalued properties with strong ROI potential"
    },
    {
      icon: Building2,
      title: "Commercial Leasing",
      description: "Retail, office, industrial, and flex space leasing"
    },
    {
      icon: MapPin,
      title: "Market Analysis",
      description: "Deep knowledge of El Paso's commercial landscape"
    }
  ];

  const clientTypes = [
    "Local Entrepreneurs",
    "Established Business Owners", 
    "Medical Professionals",
    "Restaurant Operators",
    "Property Investors",
    "Corporate Tenants"
  ];

  const approach = [
    {
      icon: Target,
      title: "Strategic Focus",
      description: "Every transaction is approached with strategy and integrity, structuring creative solutions when needed."
    },
    {
      icon: Handshake,
      title: "Trust & Partnership",
      description: "Building long-term relationships based on trust, education, and partnership with every client."
    },
    {
      icon: Award,
      title: "Client Education",
      description: "Empowering clients to make confident, informed decisions by ensuring they understand all options."
    },
    {
      icon: Globe,
      title: "Bilingual Service",
      description: "Fluent in English and Spanish, serving El Paso's diverse business community effectively."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img 
                src="./images/KWCommercial_Stacked_RGB.png" 
                alt="KW Commercial" 
                className="h-12 w-auto"
              />
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="/" className="text-foreground hover:text-primary transition-smooth">Home</a>
              <a href="/services" className="text-foreground hover:text-primary transition-smooth">Services</a>
              <a href="/properties" className="text-foreground hover:text-primary transition-smooth">Asset Types</a>
              <a href="/about" className="text-primary font-semibold">About</a>
              <a href="/contact" className="text-foreground hover:text-primary transition-smooth">Contact</a>
            </div>
            <Button className="btn-primary">
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section with Photo */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold mb-6">About Angel Cazares</h1>
              <p className="text-xl text-muted-foreground mb-8">
                As a licensed commercial real estate agent based in El Paso and proudly affiliated with KW Commercial, 
                Angel Cazares transforms property challenges into opportunities. His focus is helping business owners, 
                investors, and property owners create lasting value.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="btn-primary">
                  Schedule Consultation <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline">
                  View Services
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="./images/Untitled design.jpg" 
                alt="Angel Cazares - Commercial Real Estate Agent"
                className="rounded-lg shadow-elegant w-full max-w-md mx-auto"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-lg shadow-card">
                <div className="text-center">
                  <p className="font-semibold text-primary">Licensed Agent</p>
                  <p className="text-sm text-muted-foreground">KW Commercial</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Expertise & Services</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Angel brings deep knowledge of El Paso's commercial landscape to every client relationship. 
              His specialties span across all major commercial real estate sectors.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {expertise.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="shadow-card hover:shadow-hover transition-smooth text-center">
                  <CardHeader>
                    <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                      <Icon className="h-8 w-8 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>
                      {item.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Client-Focused Approach */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">A Strategic, Client-Focused Approach</h2>
            <p className="text-xl text-muted-foreground max-w-4xl mx-auto">
              Angel believes that great business is built on trust, education, and partnership. His role is not just 
              to broker deals but to be a long-term advisor and advocate for clients.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {approach.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="shadow-card">
                  <CardHeader>
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mr-4">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <CardTitle className="text-xl">{item.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">
                      {item.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process & Clients */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-6">Comprehensive Process</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Angel's approach is hands-on and proactive. He guides clients through every phase of the 
                commercial real estate process, from property identification through closing.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center mr-4 text-sm font-semibold">1</div>
                  <span>Property identification and market analysis</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center mr-4 text-sm font-semibold">2</div>
                  <span>Due diligence and property evaluation</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center mr-4 text-sm font-semibold">3</div>
                  <span>Strategic negotiation and deal structuring</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center mr-4 text-sm font-semibold">4</div>
                  <span>Transaction management through closing</span>
                </div>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6">Diverse Client Base</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Angel serves a wide range of clients across El Paso's business community, from first-time 
                entrepreneurs to established investors seeking portfolio expansion.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {clientTypes.map((client, index) => (
                  <Badge key={index} variant="outline" className="justify-center py-2">
                    {client}
                  </Badge>
                ))}
              </div>
              <div className="mt-8 p-6 bg-primary/5 rounded-lg">
                <h3 className="font-semibold mb-2">Specialized Services Include:</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Owner financing and lease-to-own deals</li>
                  <li>• Off-market opportunity identification</li>
                  <li>• Skip tracing and digital marketing</li>
                  <li>• Bilingual communication and service</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Innovation & Market Insights */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Innovative Marketing & Industry Insights</h2>
            <p className="text-xl text-muted-foreground max-w-4xl mx-auto">
              Today's commercial real estate environment demands innovative strategies. Angel leverages advanced 
              techniques to uncover off-market opportunities and deliver actionable insights.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="shadow-card text-center">
              <CardHeader>
                <CardTitle className="text-xl text-primary">Off-Market Properties</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Advanced skip tracing and digital marketing techniques to find hidden opportunities 
                  not available to the general public.
                </p>
              </CardContent>
            </Card>
            <Card className="shadow-card text-center">
              <CardHeader>
                <CardTitle className="text-xl text-primary">Market Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Comprehensive market insights and analyses tailored specifically to El Paso's 
                  unique business landscape and opportunities.
                </p>
              </CardContent>
            </Card>
            <Card className="shadow-card text-center">
              <CardHeader>
                <CardTitle className="text-xl text-primary">Digital Innovation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Cutting-edge marketing strategies and technology to maximize property exposure 
                  and identify the best opportunities for clients.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Work with Angel?</h2>
          <p className="text-xl text-muted-foreground mb-8">
            Whether you're searching for your first retail storefront or expanding your portfolio with a major acquisition, 
            Angel Cazares is ready to transform your property challenges into opportunities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="btn-primary px-8">
              Schedule Consultation
            </Button>
            <Button size="lg" variant="outline" className="px-8">
              Contact Angel Today
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-background py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img 
                src="./images/KWCommercial_Stacked_RGB.png" 
                alt="KW Commercial" 
                className="h-12 w-auto mb-4"
              />
              <p className="text-background/80">
                Your trusted partner in El Paso commercial real estate.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-background/80">
                <li>Tenant Representation</li>
                <li>Investment Sales</li>
                <li>Commercial Leasing</li>
                <li>Market Analysis</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Asset Types</h4>
              <ul className="space-y-2 text-background/80">
                <li>Office Buildings</li>
                <li>Retail Centers</li>
                <li>Industrial Properties</li>
                <li>Development Land</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact Angel</h4>
              <ul className="space-y-2 text-background/80">
                <li>El Paso, Texas</li>
                <li>KW Commercial</li>
                <li>Bilingual (English & Spanish)</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-background/20 mt-8 pt-8 text-center text-background/60">
            <p>&copy; 2024 Angel Cazares - KW Commercial. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;